<template>
	<div id="domain-results" class="container panel" v-tooltip-container>
		<div class="row">
			<div class="col-md-3 sidebar">
				<side-bar></side-bar>
			</div>

			<div class="col-md-9 content">
				<dash-head class="mb-3" :title="name" subtitle="Password Security Portal"></dash-head>		

				<error :error="error"></error>
				
				<table-actions placeholder="search results" @search-changed="searchChanged" @edit-click="editClicked" @delete-click="deleteClicked"
					:editEnabled="checkedRows.length === 1" :deleteEnabled="checkedRows.length > 0">
				</table-actions>
			
				<table-view id="domain-results">
					<table-row slot="header">
						<table-header></table-header>
						<table-header @click-header="sortByEmailName" sort="ascending">Email Name</table-header>
        				<table-header>Passwords</table-header>
        				<table-header>Sources</table-header>
        				<table-header @click-header="sortByScore" sort="none">Score</table-header>
					</table-row>
					<table-row v-if="loading" slot="body">
						<td colspan="5" class="spinner"><div><span></span></div></td>
					</table-row>
					<table-row slot="body" v-for="result in results" :key="result.name">
						<table-cell><input type="checkbox" class="select-row" :value="result.name" v-model="checkedRows"></table-cell>
						<table-cell><a :href="'#/domain/email/' + result.name + '@' + name" :class="{disabled:result.disabled}">{{result.name}}</a></table-cell>
						<table-cell><password-link v-for="(item, i) in result.passwords" :key="i" :password="item" :fullPassword="result.fullPasswords[i]"></password-link></table-cell>
						<table-cell><source-tooltip v-for="source in result.sourcesDetail" :key="source" :source="source"></source-tooltip></table-cell>
						<table-cell>{{result.statusText}}</table-cell>
					</table-row>
				</table-view>

				<div class="row">
					<div class="col-md-6">
						<table-pagination ref="tablePagination" :page="page" :total="totalRows" :pageSize="pageSize" @page-changed="pageChanged"></table-pagination>
					</div>
					<div class="col-md-6">
						<div class="text-right">
							<button v-if="!loading" type="button" class="btn btn-outline-primary px-3" @click="saveCsv" :disabled="continuation">Export</button>
							<button v-if="continuation" type="button" class="btn btn-outline-primary px-3" @click="loadMore" :disabled="loadingMore">Load More</button>
						</div>
					</div>
				</div>

			</div>
		</div>	
	</div>
</template>

<script>
	import SideBar from './SideBar'
	import DashHead from './DashHead'
	import TableActions from './TableActions'
	import TableView from './TableView'
	import TableRow from './TableRow'
	import TableHeader from './TableHeader'
	import TableCell from './TableCell'
	import TablePagination from './TablePagination'
	import PasswordLink from './PasswordLink'
	import SourceTooltip from './SourceTooltip'
	import Error from './Error'

	import api from '../api'
	import router from '../router'
	import store from '../state'

	export default {
		name: 'DomainResultsPanel',
		props: ['pageSize'],
		components: {SideBar, DashHead, TableActions, TableView, TableRow, TableHeader, TableCell, TablePagination, PasswordLink, SourceTooltip, Error},
		data() {
			return {
				continuation: null,
				loading:true,
				loadingMore: false,
				data: null,
				error: null,
				checkedRows: [],
				search: '',
				page:1
			}
		},
		computed: {
			name() {
				return router.currentRoute.params.name;
			}, 
			showPasswords() {
				return store.state.user.showPasswords;
			},
			filteredData() {
				if (!this.data) return [];
				if (!this.search) return this.data;
				return this.data.filter(value => value.name.includes(this.search));
			},
			totalRows() {
				return this.filteredData.length;
			},
			results() {
				const start = (this.page-1) * this.pageSize;		
				const sliced = [];								

				for (var result of this.filteredData.slice(start, start + this.pageSize)) {
					
					//Ignore deleted records (api should also ignore them)
					if (result.userStatus === 4) continue;

					//Because of excludedPasswords, we need to exclude passwords, fullPasswords and sources that are in the list
					if (result.excludedPasswordIndexes) {

						//Sort the indexes so that the multi splice algorithm will work correctly
						result.excludedPasswordIndexes.sort(function(a,b){ return b - a; });

						//Copy arrays over results
						spliceArrayMultiple(result.passwords, result.excludedPasswordIndexes);
						spliceArrayMultiple(result.fullPasswords, result.excludedPasswordIndexes);
						spliceArrayMultiple(result.sources, result.excludedPasswordIndexes);
					}

					//Combine all sources
					var sourceResults = [];
					for (var sourceItems of result.sources) {
						for (var item of sourceItems) {

							//If we have an item, then split it up into it's parts and put it in the array
							if (item.length) sourceResults.push(...item.split(';'));
						}
					}			

					//Ensure the array contains only unique sources
					sourceResults = sourceResults.filter((v,i,a)=>a.indexOf(v)==i);

					let sources = [];
					for (var sourceResult of sourceResults) {
						sources.push(store.getters.getSource(sourceResult));
					}

					//Now we want to get the earliest non combo source
					sources.sort((a,b) => a.date - b.date);

					//We loop through until we have a non combo
					const breachSources = [];
					const comboSources = [];

					for (var source of sources) {
						if (source.type === 'Breach') {
							breachSources.push(source.rowKey)
						}
						else {
							//Just add first one
							if (!comboSources.length) comboSources.push(source.rowKey)
						}
					}

					result.sourcesDetail = (breachSources.length) ? breachSources: comboSources;
					result.disabled = (result.userStatus === 2 || result.userStatus === 3 || result.userStatus === 4);
					result.statusText = result.score;
					
					if (result.userStatus === 2) result.statusText = '(disabled)';
					if (result.userStatus === 3) result.statusText = '(expired)';
					//if (result.userStatus === 4) result.statusText = '(deleted)';
					
					result.checked = false;

					sliced.push(result);
				}
				return sliced;
			}
		},
		methods: {
			editClicked(value) {
				router.push({name: 'domainEmailDetailEdit', params: {name: `${this.checkedRows[0]}@${this.name}`}});
			},
			saveCsv() {
				let csvContent = 'data:text/csv;charset=utf-8,';
				csvContent += 'name,passwords,sources,score\r\n';

				for (var result of this.filteredData) {

					var sourceResults = [];
					for (var sourceItems of result.sources) {
						for (var item of sourceItems) {
							if (item.length && sourceResults.indexOf(item) === -1) sourceResults.push(...item.split(';'));
						}
					}				

					const row = `"${result.name}@${this.name}","${result.passwords.join(" ")}","${sourceResults.join(' ')}","${result.score}"`;
					csvContent += row + '\r\n';
				}

				//Create a temporary link in html directly
				let encodedUri = encodeURI(csvContent),
					link = document.createElement('a');

				//Encode # characters
				encodedUri = encodedUri.replace(new RegExp('#', 'g'), '%23');

				link.setAttribute('href', encodedUri);
				link.setAttribute('download', `${this.name.replace('.','-')} credentials.csv`);
				document.body.appendChild(link); // Required for FF

				link.click();
				link.parentNode.removeChild(link);
			},
			async deleteClicked(value)
			{						
				try {
					for (let row of this.checkedRows) {
						await api.delete(`domain/email/${row}@${this.name}`);	
						
						//Loop through the results and change the status
						let count = 0;
						for (var result of this.data) {
							if (result.name === row) {
								result.userStatus = 4; //mark as deleted
								this.page = Math.floor(count / this.pageSize) + 1;

								break;
							}
							count++;
						}
					}
				}
				catch(err) {				
					this.error = err;
				}
			},
			sortByEmailName(direction) {
				this.sortByProperty(direction, 'name');
			},
			sortByScore(direction) {
				this.sortByPropertyNumeric(direction, 'score');
			},
			sortByProperty(direction, name) {		
				if (direction === 'ascending') {
					this.data.sort(function (a,b) {
						if (a[name] === b[name]) return 0;
						return (a[name] > b[name]) ? 1 : -1;
					});
				}
				else {
					this.data.sort(function (a,b) {
						if (b[name] === a[name]) return 0;
						return (b[name] > a[name]) ? 1 : -1;
					});

				}
				this.page = 1;
			},
			sortByPropertyNumeric(direction, name) {				
				if (direction === 'ascending') {
					this.data.sort((a,b) => a[name] - b[name]);
				}
				else {					
					this.data.sort((a,b) => b[name] - a[name]);
				}
				this.page = 1;
			},
			searchChanged(value) {
				this.search = value.toLowerCase();
				this.page = 1;
			},
			pageChanged(value) {
				this.page = value;
			},
			async loadMore() {
				this.loadingMore = true;
				try {
					const name = router.currentRoute.params.name;
					const response = await api.post(`domain/${name}`, this.continuation);					

					this.data.push(...response.data.item1);
					this.continuation = response.data.item2;
				}
				catch(err) {				
					this.error = err;
				}
				finally {
					this.loadingMore = false;
				}	
			}
		},
		async mounted() {
			try {
				const name = router.currentRoute.params.name;
				const response = await api.get(`domain/${name}`);					

				this.data = response.data.item1;
				this.continuation = response.data.item2;
				this.page = 1;
			}
			catch(err) {				
				this.error = err;
			}
			finally {
				this.loading = false;
			}
		}
	}

	//Note indexes must be sorted
	function spliceArrayMultiple(values, indexes) {
		for (var i = indexes.length -1; i >= 0; i--) values.splice(indexes[i],1);   		
	}
</script>


// WEBPACK FOOTER //
// src/components/DomainResultsPanel.vue